import { Form } from "antd"
import { useWatch } from "antd/es/form/Form"
import { Group, InputNumber, Legend, Modal, SelectInput, TextInput } from "components/common"
import { useLazyGetProjectSectionsQuery, useLazyGetProjectSectionsWithParentQuery } from "features/inventory/api"
import { useLazyGetUnitTypesWithSubTypesQuery } from "features/projects/api"
import { useLazyGetAreaUnitsQuery } from "features/shared/api"
import { t } from "i18next"
import { forwardRef } from "react"

const ProvisionalSaleModal = forwardRef((props, ref) => {
  const { parentForm, projects, projectId, setParentState } = props
  const [form] = Form.useForm()

  const [getAreaUnit, { data: areaUnit }] = useLazyGetAreaUnitsQuery()
  const [getUnitType, { data: units }] = useLazyGetUnitTypesWithSubTypesQuery()
  const [getProjectSectionList, { data: projectSectionList }] = useLazyGetProjectSectionsQuery()
  const [getProjectSectionListWithParent, { data: projectSectionListWithParent, isFetching: fetchProjectWithParent }] =
    useLazyGetProjectSectionsWithParentQuery()

  const onModalVisible = () => {
    getAreaUnit(null, true)
    getUnitType(null, true)
    getProjectSectionList(null, true)
    getProjectSectionListWithParent(null, true)
  }

  const section = useWatch("section", form)
  const basePrice = useWatch("basePrice", form)

  return (
    <Modal
      title={t("Add Provisional Unit")}
      ref={ref}
      okText={t("Next")}
      width={900}
      cancelText={t("Cancel")}
      onModalVisible={onModalVisible}
      onCancel={() => {
        ref?.current?.setVisible(false)
        form.resetFields()
      }}
      onOk={() => {
        form.validateFields().then(values => {
          parentForm.setFieldValue("unit", {
            name: values?.unitNumber,
            unitPrice: values?.price,
            id: "Provisional",
            FeatureCharges: [],
            value: values?.unitNumber,
          })

          setParentState({
            provisionalUnit: {
              ...values,
              basePrice: {
                value: values?.basePrice,
              },
              price: {
                value: values?.price,
              },
              locationSectionId: +values?.section ?? +values?.subSection,
              typeId: +values?.typeId,
            },
            unitPrice: values?.basePrice,
            dealPrice: values?.price,
            provisionalFormFilled: true,
          })
          parentForm.resetFields(["amount", "modeOfPayment", "nextTask", "deadline"])
          ref?.current?.setVisible(false)
        })
      }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Legend title={t("Select Project & Unit")} />
        <Form.Item label={t("Select Project")} rules={[{ required: true }]} required>
          <SelectInput options={projects} value={projectId} disabled />
        </Form.Item>

        <Group className="mbe-16" template="repeat(2, 1fr)" gap="16px 32px">
          <Form.Item
            label={t("Unit Number")}
            name={"unitNumber"}
            rules={[{ required: true, message: t("Please enter unit number") }]}
            required
          >
            <TextInput placeholder={t("Please add Unit Number")} />
          </Form.Item>
          <Form.Item
            label={t("Base Price")}
            name={"basePrice"}
            rules={[{ required: true, message: t("Please enter base price") }]}
            required
          >
            <InputNumber min={0} style={{ width: "100%" }} placeholder={t("Please enter base price")} />
          </Form.Item>
          <Form.Item
            label={t("Published Price")}
            name={"price"}
            rules={[
              { required: true, message: t("Please enter published price") },
              {
                validator: (_, value) => {
                  if (basePrice > value) {
                    return Promise.reject(t("Published price cannot be less than base price"))
                  }
                  return Promise.resolve()
                },
              },
            ]}
            required
          >
            <InputNumber min={0} style={{ width: "100%" }} placeholder={t("Please enter published price")} />
          </Form.Item>
          <Form.Item
            label={t("Area Unit")}
            name={"areaUnitId"}
            rules={[{ required: true, message: t("Please enter area unit") }]}
            required
          >
            <SelectInput
              placeholder={t("Please select Area unit")}
              options={areaUnit}
              getOptionValue={item => item?.id}
            />
          </Form.Item>
          <Form.Item
            label={t("Land Area")}
            name={"landArea"}
            rules={[{ required: true, message: t("Please enter land area") }]}
            required
          >
            <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder={t("Please enter land area")} />
          </Form.Item>
          <Form.Item
            label={t("Type")}
            name={"typeId"}
            rules={[{ required: true, message: t("Please enter type") }]}
            required
          >
            <SelectInput placeholder={t("Please select type")} options={units} />
          </Form.Item>
          <Form.Item
            name="section"
            label={t("Section")}
            rules={[{ required: true, message: t("Please enter section") }]}
            required
          >
            <SelectInput
              placeholder={t("Select selection")}
              options={projectSectionList}
              onChange={(option, value) => {
                getProjectSectionListWithParent({ all: true, projectId: projectId?.id, parentId: option })
              }}
            />
          </Form.Item>
          {section && (
            <Form.Item name="subSection" label={"Sub Section"}>
              <SelectInput
                placeholder={t("Select section")}
                options={projectSectionListWithParent}
                inputLoading={fetchProjectWithParent}
              />
            </Form.Item>
          )}
        </Group>
      </Form>
    </Modal>
  )
})

export default ProvisionalSaleModal
